/**
 * AgentCredentials — Manage API keys and credentials for external services
 * Source: {dataDir}/workspace/.env
 */
import { useState, useEffect } from 'preact/hooks';
import { Spinner } from './ui';
import { isAdmin } from '../stores/auth.store';
import { api } from '../api/client';
import { INTEGRATION_ICONS } from './IntegrationIcons';
import { isDockerMode } from '../lib/openclawPaths';
import { LockOverlay } from './LockOverlay';
import { SkeletonBar } from './ui';
import { T } from '../styles/tokens';
import { Button } from './ui';

interface Props {
  runnerId: string;
  isOnline: boolean;
  openclawPath?: string;
  cloudMachineId?: string;
  dataDir?: string;
  locked?: boolean;
  editorData?: any;
}

interface Integration {
  id: string;
  name: string;
  icon: string;
  color: string;
  description: string;
  fields: IntegrationField[];
  docs?: string;
  testCommand?: string;
  howToGet?: string;
}

interface IntegrationField {
  key: string;
  label: string;
  type: 'api_key' | 'token' | 'username' | 'password' | 'email' | 'text';
  placeholder: string;
  required?: boolean;
}

// INTEGRATIONS is now populated dynamically from D1 catalog.
// Exported as mutable for backward compat with Orchestrator.tsx.
// Will be populated on first AgentCredentials mount or via fetchIntegrationsCatalog().
export let INTEGRATIONS: Integration[] = [];

let _catalogFetched = false;
export async function fetchIntegrationsCatalog(): Promise<Integration[]> {
  if (_catalogFetched && INTEGRATIONS.length > 0) return INTEGRATIONS;
  try {
    const res = await api.get('/integrations/full') as any;
    const catalog = res?.catalog || [];
    INTEGRATIONS = catalog.map((item: any) => {
      let fields: IntegrationField[] = [];
      try {
        const parsed = item.fields || (item.fields_json ? JSON.parse(item.fields_json) : []);
        fields = parsed.map((f: any) => ({
          key: f.key,
          label: f.label,
          type: f.type || 'text',
          placeholder: f.placeholder || `Insira ${f.label}`,
          required: f.required,
        }));
      } catch {}
      return {
        id: item.id,
        name: item.label,
        icon: '',
        color: item.color || T.textMuted,
        description: item.description || '',
        fields,
        docs: item.docs_url || undefined,
        testCommand: item.test_command || undefined,
        howToGet: item.how_to_get || undefined,
      };
    });
    _catalogFetched = true;
  } catch (e) {
    console.error('[AgentCredentials] Failed to fetch catalog:', e);
  }
  return INTEGRATIONS;
}

// Remove old hardcoded array — was here before refactor
const _REMOVED_HARDCODE = true; // marker for git blame
void _REMOVED_HARDCODE;

type FieldValues = Record<string, string>;
type IntegrationStatus = 'unknown' | 'configured' | 'not_configured' | 'testing' | 'verified' | 'failed';

// ─── Import types ────────────────────────────────────────────────────
interface ImportSource {
  machine_id: string;
  agent_name: string;
  machine_name: string;
  machine_status: string;
  integrations: string[];
  env_keys_count: number;
}

interface ImportRequest {
  id: string;
  source_machine_id: string;
  target_machine_id: string;
  integrations: string;
  status: string;
  result: string;
  created_at: string;
}

type ImportStep = 'closed' | 'choose-type' | 'select-source' | 'select-integrations' | 'confirm';

const IMPORT_SOURCE_TYPES = [
  { id: 'agent', label: 'De outro agente', active: true },
  { id: 'lastpass', label: 'LastPass', active: false },
  { id: '1password', label: '1Password', active: false },
  { id: 'bitwarden', label: 'Bitwarden', active: false },
  { id: 'chrome', label: 'Chrome / Google', active: false },
  { id: 'icloud', label: 'iCloud Keychain', active: false },
  { id: 'samsung', label: 'Samsung Pass', active: false },
];

function IntegIcon({ id, size = 24 }: { id: string; size?: number }) {
  const IconComponent = INTEGRATION_ICONS[id];
  if (IconComponent) return <IconComponent size={size} />;
  return <span style={{ fontSize: size * 0.8 }}>·</span>;
}

export function AgentCredentials({ runnerId, isOnline, openclawPath, cloudMachineId, dataDir = '/data/.openclaw', locked, editorData }: Props) {
  const [selectedInteg, setSelectedInteg] = useState<Integration | null>(null);
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  const [existingKeys, setExistingKeys] = useState<Record<string, string>>({});
  const [statuses, setStatuses] = useState<Record<string, IntegrationStatus>>({});
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showValues, setShowValues] = useState<Record<string, boolean>>({});
  const [showGuide, setShowGuide] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [syncStatus, setSyncStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [syncDetail, setSyncDetail] = useState<{ source: string; path: string; time: string } | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);

  // Import state
  const [importStep, setImportStep] = useState<ImportStep>('closed');
  const [importSources, setImportSources] = useState<ImportSource[]>([]);
  const [importSourcesLoading, setImportSourcesLoading] = useState(false);
  const [selectedSource, setSelectedSource] = useState<ImportSource | null>(null);
  const [selectedImportIntegs, setSelectedImportIntegs] = useState<Set<string>>(new Set());
  const [importProcessing, setImportProcessing] = useState(false);
  const [importResult, setImportResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const [importHistory, setImportHistory] = useState<ImportRequest[]>([]);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<ImportRequest | null>(null);

  // Sync connected integrations to D1
  async function syncIntegrationsToD1(statusMap: Record<string, IntegrationStatus>) {
    const machineId = cloudMachineId || runnerId;
    if (!machineId) return;
    try {
      const connected = Object.entries(statusMap)
        .filter(([_, s]) => s === 'configured' || s === 'verified')
        .map(([id]) => id);
      await api.put(`/cloud/machines/${machineId}/agent-info`, {
        integrations: JSON.stringify(connected),
      });
      console.log('[Integrations] Synced to D1:', connected.length, 'connected');
    } catch (e) {
      console.warn('[Integrations] D1 sync failed:', e);
    }
  }

  // ─── Import functions ────────────────────────────────────────────────
  async function loadAvailableSources() {
    setImportSourcesLoading(true);
    try {
      const machineId = cloudMachineId || runnerId;
      const res = await api.get(`/credential-import/available-sources?exclude=${machineId}`) as any;
      console.log('[Import] available-sources response:', JSON.stringify(res));
      const sources = res?.sources || res?.data?.sources || [];
      console.log('[Import] parsed sources:', sources.length, 'machineId excluded:', machineId);
      setImportSources(sources);
    } catch (e: any) {
      console.warn('[Import] Failed to load sources:', e);
      setImportSources([]);
    } finally {
      setImportSourcesLoading(false);
    }
  }

  async function loadImportHistory() {
    try {
      const machineId = cloudMachineId || runnerId;
      const res = await api.get(`/credential-import/requests?target_machine_id=${machineId}`) as any;
      const requests = (res?.requests || res?.data?.requests || []);
      setImportHistory(requests.slice(0, 5));
      // Check for pending/processing/failed import
      // Any status except 'completed' and 'cancelled' blocks the tab (failed = will retry)
      const active = requests.find((r: any) => r.status !== 'completed' && r.status !== 'cancelled');
      setPendingImport(active || null);
    } catch (e: any) {
      console.warn('[Import] Failed to load history:', e);
    }
  }

  async function handleImportSubmit() {
    if (!selectedSource || selectedImportIntegs.size === 0) return;
    setImportProcessing(true);
    setImportResult(null);
    try {
      const machineId = cloudMachineId || runnerId;
      const res = await api.post('/credential-import/request', {
        source_machine_id: selectedSource.machine_id,
        target_machine_id: machineId,
        integrations: Array.from(selectedImportIntegs),
      }) as any;
      const data = res?.data || res;
      const status = data?.status || 'unknown';
      if (status === 'completed' || status === 'partial' || status === 'pending') {
        const msg = status === 'pending' 
          ? 'Importação enfileirada! Será processada automaticamente.'
          : `Importação ${status === 'completed' ? 'concluída' : 'parcial'}! ${data?.result?.imported_keys || 0} chaves importadas.`;
        setImportResult({ ok: true, msg });
        setImportStep('closed');
        // Reload credentials (pending will update via cron)
        if (status !== 'pending') {
          await loadExistingKeys();
        }
        await loadImportHistory();
      } else {
        setImportResult({ ok: false, msg: data?.result?.error || 'Falha na importação' });
      }
    } catch (e: any) {
      setImportResult({ ok: false, msg: e?.message || 'Erro ao importar' });
    } finally {
      setImportProcessing(false);
      setTimeout(() => setImportResult(null), 8000);
    }
  }

  function openImportPanel() {
    setImportStep('choose-type');
    setSelectedSource(null);
    setSelectedImportIntegs(new Set());
    setImportResult(null);
    loadImportHistory();
  }

  // runCommand/runCommandDirectOnly removed — all operations via D1/API now

  // ─── LOAD ──────────────────────────────────────────────────────────────
  async function loadExistingKeys() {
    setLoading(true);
    setSyncStatus('loading');
    setSyncError(null);
    try {
      // Strategy 0: Use editorData from D1 (instant, no SSH)
      if (editorData?.synced && editorData.credentials) {
        console.log('[Integrations] Loading from D1 editorData (instant)');
        const newStatuses: Record<string, IntegrationStatus> = {};
        const newKeys: Record<string, string> = {};
        // credentials = [{ provider: 'github', keys: ['GITHUB_TOKEN', ...] }, ...]
        const credProviders = new Set((editorData.credentials || []).map((c: any) => c.provider));
        for (const integ of INTEGRATIONS) {
          const cred = (editorData.credentials || []).find((c: any) => c.provider === integ.id);
          if (cred && cred.keys?.length > 0) {
            newStatuses[integ.id] = 'configured';
            for (const key of cred.keys) {
              newKeys[key] = '••••••••'; // masked — use decrypt endpoint to view
            }
          } else {
            newStatuses[integ.id] = 'not_configured';
          }
        }
        // Also check env_keys from lists
        const envKeys = editorData.lists?.env_keys || [];
        for (const key of envKeys) {
          if (!newKeys[key]) newKeys[key] = '••••••••';
        }
        setExistingKeys(newKeys);
        setStatuses(newStatuses);
        setSyncStatus('success');
        const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        setSyncDetail({ source: 'D1 database', path: 'machine_integrations', time: now });
        setLoading(false);
        return;
      }

      // No SSH fallback — editorData must be synced first
      console.warn('[Integrations] editorData not available, showing empty state');
      setSyncStatus('success');
      const now = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
      setSyncDetail({ source: 'Aguardando sincronizacao', path: 'D1 database', time: now });
    } catch (err: any) {
      console.error('[Integrations] Failed to load env:', err);
      setSyncStatus('error');
      setSyncError(err?.message || 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }

  const [catalogLoaded, setCatalogLoaded] = useState(INTEGRATIONS.length > 0);

  useEffect(() => {
    // Fetch catalog from D1 if not yet loaded
    if (!catalogLoaded) {
      fetchIntegrationsCatalog().then(() => setCatalogLoaded(true));
    }
    if (isOnline) loadExistingKeys();
    loadImportHistory(); // Check for pending imports on mount
  }, [runnerId, isOnline, openclawPath, cloudMachineId]);

  // Auto-refresh when there's a pending import
  useEffect(() => {
    if (!pendingImport) return;
    const interval = setInterval(() => {
      loadImportHistory();
      if (isOnline) loadExistingKeys(); // Refresh credentials when import completes
    }, 15000);
    return () => clearInterval(interval);
  }, [pendingImport]);

  // ─── SAVE ──────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!selectedInteg) return;
    const keysToUpdate = Object.entries(fieldValues).filter(([_, v]) => v.trim());
    if (keysToUpdate.length === 0) {
      setSaveResult({ ok: false, msg: 'Nenhum campo preenchido' });
      setTimeout(() => setSaveResult(null), 3000);
      return;
    }

    setSaving(true);
    setSaveResult(null);

    try {
      // Build keys to save
      const keysObj: Record<string, string> = {};
      for (const [key, val] of keysToUpdate) keysObj[key] = val.trim();

      if (cloudMachineId) {
        // Runner-side merge via queue (no SSH read needed)
        await api.post(`/cloud/machines/${cloudMachineId}/queue-action`, {
          action: 'env_save',
          payload: { keys: keysObj, mode: 'merge' },
          source: 'credentials_editor',
        });
        console.log('[Integrations] env_save queued for', cloudMachineId);
      } else {
        throw new Error('Cloud machine ID nao disponivel — nao e possivel salvar');
      }

      setSaveResult({ ok: true, msg: `${selectedInteg.name} — ${keysToUpdate.length} credencial(is) salva(s)!` });
      setFieldValues({});
      await new Promise(r => setTimeout(r, 1000));
      await loadExistingKeys();
    } catch (err: any) {
      const msg = err?.message || 'Erro desconhecido';
      const isOffline = msg.includes('offline') || msg.includes('503') || msg.includes('not_connected');
      setSaveResult({
        ok: false,
        msg: isOffline
          ? 'Máquina offline — runner não conectado. Verifique se está rodando.'
          : `Erro ao salvar: ${msg}`
      });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveResult(null), 6000);
    }
  }

  // ─── TEST CONNECTION ───────────────────────────────────────────────────
  async function handleTest(integ: Integration) {
    if (!integ.testCommand) return;
    // Test via queue action (runner executes test command)
    setStatuses(s => ({ ...s, [integ.id]: 'testing' }));
    try {
      if (!cloudMachineId) throw new Error('No cloud machine');
      await api.post(`/cloud/machines/${cloudMachineId}/queue-action`, {
        action: 'integration_test',
        payload: { provider: integ.id, testCommand: integ.testCommand },
        source: 'credentials_editor',
      });
      // Since test is async via queue, show as configured (user can check later)
      setStatuses(s => ({ ...s, [integ.id]: 'configured' }));
    } catch {
      setStatuses(s => ({ ...s, [integ.id]: 'failed' }));
    }
  }

  // ─── DISCONNECT ────────────────────────────────────────────────────────
  async function handleDisconnect(integ: Integration) {
    setSaving(true);
    setSaveResult(null);
    try {
      if (!cloudMachineId) throw new Error('Cloud machine ID nao disponivel');

      // Queue removal of keys via runner
      const keysToRemove = integ.fields.map(f => f.key);
      await api.post(`/cloud/machines/${cloudMachineId}/queue-action`, {
        action: 'env_save',
        payload: { keys_to_remove: keysToRemove },
        source: 'credentials_editor',
      });

      setSaveResult({ ok: true, msg: `${integ.name} desconectado!` });
      await loadExistingKeys();
    } catch (err: any) {
      setSaveResult({ ok: false, msg: `Erro: ${err.message}` });
    } finally {
      setSaving(false);
      setTimeout(() => setSaveResult(null), 3000);
    }
  }

  // ─── STATUS HELPERS ────────────────────────────────────────────────────
  function getStatusIcon(integId: string) {
    const st = statuses[integId] || 'unknown';
    if (st === 'configured' || st === 'verified') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="10" fill="#10B981"/>
          <path d="M6 10.5l2.5 2.5 5.5-5.5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    }
    if (st === 'testing') {
 return <span style={{ fontSize: 14 }}></span>;
    }
    if (st === 'failed') {
      return (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <circle cx="10" cy="10" r="10" fill="#EF4444"/>
          <path d="M7 7l6 6M13 7l-6 6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      );
    }
    return null;
  }

  function getStatusBadge(integId: string) {
    const st = statuses[integId] || 'unknown';
    const map: Record<string, { color: string; bg: string; text: string }> = {
 unknown: { color: 'var(--text-muted)', bg: T.bgActive, text: 'Desconhecido' },
 not_configured: { color: T.danger, bg: T.dangerBg, text: 'Desconectado' },
 configured: { color: T.success, bg: T.successBg, text: 'Conectado' },
 testing: { color: 'var(--primary)', bg: T.bgCard, text: 'Testando...' },
 verified: { color: T.success, bg: T.successBg, text: 'Verificado' },
 failed: { color: T.danger, bg: T.dangerBg, text: 'Falhou' },
    };
    const s = map[st] || map['unknown'];
    return (
      <span style={{
        fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 6,
        color: s!.color, background: s!.bg, border: `1px solid ${s!.color}22`,
      }}>{s!.text}</span>
    );
  }

  // ─── OFFLINE STATE ─────────────────────────────────────────────────────
  if (!isOnline) {
    return (
      <div style={{ padding: 24, marginBottom: 20, borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border)', textAlign: 'center', color: 'var(--text-muted)' }}>
 <span style={{ fontSize: 32 }}></span>
        <p style={{ margin: '8px 0 0' }}>Máquina offline. Conecte para configurar integrações.</p>
      </div>
    );
  }

  // ─── FILTERED INTEGRATIONS ─────────────────────────────────────────────
  const filteredIntegrations = INTEGRATIONS.filter(integ => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return integ.name.toLowerCase().includes(q) || integ.description.toLowerCase().includes(q);
  });

  const connectedCount = Object.values(statuses).filter(s => s === 'configured' || s === 'verified').length;

  // ─── RENDER ────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}>
      <LockOverlay locked={locked} />
      {/* Search + status bar */}
      {/* Sync status bar */}
      <div style={{
        padding: '6px 12px',
        marginBottom: 0,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        fontSize: 11,
      }}>
        {syncStatus === 'loading' && (
          <div style={{ color: T.warning, display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{
              width: 10, height: 10, border: '1.5px solid #EAB308',
              borderTopColor: 'transparent', borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
          </div>
        )}
        {syncStatus === 'success' && syncDetail && (
          <div style={{ color: T.success }}>
            {syncDetail.source} · {syncDetail.path} · {syncDetail.time}
          </div>
        )}
        {syncStatus === 'error' && (
          <div style={{ color: T.danger }}>
            Erro: {syncError || 'falha ao carregar'}
          </div>
        )}
        {syncStatus === 'idle' && (
          <div style={{ color: 'var(--text-muted)' }}>
            Aguardando conexao...
          </div>
        )}
      </div>

      {/* Search + count bar */}
      <div style={{
        padding: '8px 12px',
        marginBottom: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          {connectedCount} conectadas · {INTEGRATIONS.length} disponíveis
        </div>
        {!selectedInteg && (
          <input
            type="search"
            placeholder="Buscar..."
            value={searchQuery}
            onInput={(e: any) => setSearchQuery(e.target.value)}
            style={{
              padding: '5px 10px', borderRadius: 6, flex: '0 1 180px',
              background: T.bgCardHover, border: '1px solid var(--border)',
              color: 'var(--text)', fontSize: 12, outline: 'none',
            }}
          />
        )}
      </div>

      {/* Toast feedback */}
      {saveResult && (
        <div style={{
          padding: '6px 12px', fontSize: 12, fontWeight: 600,
          background: saveResult.ok ? T.successBg : T.dangerBg,
          color: saveResult.ok ? T.success : T.danger,
          transition: 'all 0.3s',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span>{saveResult.msg}</span>
          {!saveResult.ok && (
            <Button variant="ghost" onClick={handleSave} style={{
              background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
              color: T.danger, borderRadius: 4, padding: '2px 8px', cursor: 'pointer',
              fontSize: 11, fontWeight: 600,
            }}>Tentar novamente</Button>
          )}
        </div>
      )}

      {/* ═══ PENDING IMPORT BANNER ═══ */}
      {pendingImport && importStep === 'closed' && (
        <div style={{
          margin: '0 0 12px', padding: '20px 16px',
          background: `linear-gradient(135deg, ${T.primaryBgSubtle}, ${T.purpleBgSubtle})`,
          border: `1px solid ${T.primaryBgStrong}`,
          borderRadius: 10, textAlign: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, marginBottom: 8 }}>
            <Spinner size="sm" inline />
            <span style={{ fontSize: 14, fontWeight: 700, color: T.text }}>
              Importação de credenciais em andamento
            </span>
          </div>
          <p style={{ fontSize: 12, color: T.textSecondary, margin: '0 0 6px', lineHeight: 1.5 }}>
            Suas credenciais estão sendo importadas automaticamente. Isso leva apenas alguns minutos.
            {pendingImport.status === 'failed' && ' O sistema vai tentar novamente em breve.'}
          </p>
          <p style={{ fontSize: 11, color: T.textMuted, margin: 0 }}>
            Status: <span style={{ color: T.primary, fontWeight: 600 }}>
              {pendingImport.status === 'pending' ? 'Na fila — aguardando processamento'
                : pendingImport.status === 'failed' ? 'Tentativa anterior falhou — nova tentativa em breve'
                : 'Processando...'}
            </span>
          </p>
          {(() => {
            let integs: string[] = [];
            try { integs = JSON.parse((pendingImport as any).integrations || '[]'); } catch {}
            return integs.length > 0 ? (
              <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                {integs.map((i: string) => (
                  <span key={i} style={{ padding: '2px 8px', borderRadius: 6, fontSize: 10, fontWeight: 600, background: T.primaryBg, color: '#93C5FD' }}>{i}</span>
                ))}
              </div>
            ) : null;
          })()}
        </div>
      )}

      {/* ═══ IMPORT PANEL ═══ */}
      {importStep !== 'closed' && !pendingImport && (
        <div style={{
          margin: '0 0 8px', padding: '12px',
          background: T.bgCard, borderBottom: `1px solid ${T.whiteAlpha8}`,
          transition: 'all 0.2s',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {importStep !== 'choose-type' && (
                <Button variant="ghost" onClick={() => {
                  if (importStep === 'select-source') setImportStep('choose-type');
                  else if (importStep === 'select-integrations') setImportStep('select-source');
                  else if (importStep === 'confirm') setImportStep('select-integrations');
                }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14, padding: '2px 4px' }}>←</Button>
              )}
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>Importar Credenciais</span>
              <span style={{ fontSize: 10, color: 'var(--text-muted)', background: T.border, padding: '2px 6px', borderRadius: 4 }}>
                {importStep === 'choose-type' ? '1/4' : importStep === 'select-source' ? '2/4' : importStep === 'select-integrations' ? '3/4' : '4/4'}
              </span>
            </div>
            <Button variant="ghost" onClick={() => setImportStep('closed')} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12 }}></Button>
          </div>

          {/* Step 1: Choose source type */}
          {importStep === 'choose-type' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {IMPORT_SOURCE_TYPES.map(t => (
                <Button variant="ghost"
                  key={t.id}
                  disabled={!t.active}
                  onClick={() => { if (t.active) { setImportStep('select-source'); loadAvailableSources(); } }}
                  style={{
                    padding: '10px 12px', borderRadius: 8, textAlign: 'left',
                    background: t.active ? T.bgCard : T.bgSubtle,
                    border: t.active ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(255,255,255,0.04)',
                    color: t.active ? 'var(--text)' : 'var(--text-muted)',
                    cursor: t.active ? 'pointer' : 'default',
                    opacity: t.active ? 1 : 0.5,
                    position: 'relative',
                  }}
                >
                  <div style={{ fontSize: 12, fontWeight: 600 }}>{t.label}</div>
                  {!t.active && (
                    <span style={{ fontSize: 9, color: T.warning, background: T.warningBg, padding: '1px 5px', borderRadius: 3, marginTop: 4, display: 'inline-block' }}>Em breve</span>
                  )}
                </Button>
              ))}
            </div>
          )}

          {/* Step 2: Select source agent */}
          {importStep === 'select-source' && (
            <div>
              {importSourcesLoading ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  <div style={{ width: 14, height: 14, border: '2px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 8px' }} />
                  Buscando agentes...
                </div>
              ) : importSources.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                  Nenhum agente com credenciais encontrado.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>Selecione o agente de origem:</div>
                  {importSources.map(src => (
                    <Button variant="ghost"
                      key={src.machine_id}
                      onClick={() => { setSelectedSource(src); setSelectedImportIntegs(new Set()); setImportStep('select-integrations'); }}
                      style={{
                        padding: '10px 12px', borderRadius: 8, textAlign: 'left',
                        background: T.bgCard, border: `1px solid ${T.whiteAlpha8}`,
                        color: 'var(--text)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
                      }}
                    >
                      <div style={{ width: 32, height: 32, borderRadius: 8, background: T.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-7 8-7s8 3 8 7"/></svg>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{src.agent_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{src.machine_name} · {src.integrations.length} integrações</div>
                      </div>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.3 }}>
                        <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Select integrations */}
          {importStep === 'select-integrations' && selectedSource && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
                De: <strong style={{ color: 'var(--text)' }}>{selectedSource.agent_name}</strong> · Selecione as integrações:
              </div>
              {/* Select all */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', marginBottom: 4, borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  checked={selectedImportIntegs.size === selectedSource.integrations.length}
                  onChange={(e: any) => {
                    if (e.target.checked) setSelectedImportIntegs(new Set(selectedSource.integrations));
                    else setSelectedImportIntegs(new Set());
                  }}
                  style={{ accentColor: 'var(--primary)' }}
                />
                Selecionar todas ({selectedSource.integrations.length})
              </label>
              {/* Integration list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedSource.integrations.map(integId => {
                  const integ = INTEGRATIONS.find(i => i.id === integId);
                  const targetHas = statuses[integId] === 'configured' || statuses[integId] === 'verified';
                  return (
                    <label
                      key={integId}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer', borderRadius: 4 }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedImportIntegs.has(integId)}
                        onChange={() => {
                          const next = new Set(selectedImportIntegs);
                          if (next.has(integId)) next.delete(integId); else next.add(integId);
                          setSelectedImportIntegs(next);
                        }}
                        style={{ accentColor: 'var(--primary)' }}
                      />
                      <div style={{ width: 22, height: 22, borderRadius: 4, background: `${integ?.color || T.textMuted}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <IntegIcon id={integId} size={14} />
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text)', flex: 1 }}>{integ?.name || integId}</span>
                      {targetHas && (
                        <span style={{ fontSize: 9, color: T.warning, background: T.warningBg, padding: '1px 5px', borderRadius: 3 }}>já configurada</span>
                      )}
                    </label>
                  );
                })}
              </div>
              {/* Next button */}
              <Button variant="ghost"
                disabled={selectedImportIntegs.size === 0}
                onClick={() => setImportStep('confirm')}
                style={{
                  marginTop: 10, padding: '7px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  background: selectedImportIntegs.size > 0 ? T.bgActive : T.bgCard,
                  border: 'none', color: selectedImportIntegs.size > 0 ? '#fff' : 'var(--text-muted)',
                  cursor: selectedImportIntegs.size > 0 ? 'pointer' : 'not-allowed',
                }}
              >Avançar →</Button>
            </div>
          )}

          {/* Step 4: Confirm */}
          {importStep === 'confirm' && selectedSource && (
            <div>
              <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.6, marginBottom: 10 }}>
                Importar <strong>{selectedImportIntegs.size} integração(ões)</strong> de <strong>{selectedSource.agent_name}</strong>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 10 }}>
                {Array.from(selectedImportIntegs).map(id => {
                  const integ = INTEGRATIONS.find(i => i.id === id);
                  return (
                    <span key={id} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 4, background: T.border, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <IntegIcon id={id} size={12} />
                      {integ?.name || id}
                    </span>
                  );
                })}
              </div>
              <div style={{ fontSize: 11, color: T.warning, marginBottom: 10, padding: '6px 8px', borderRadius: 4, background: `${T.warningBgSubtle}`, border: `1px solid ${T.warningBg}` }}>
                Credenciais existentes serão sobrescritas
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="ghost"
                  onClick={handleImportSubmit}
                  disabled={importProcessing}
                  style={{
                    padding: '8px 20px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                    background: importProcessing ? T.border : T.successBg,
                    border: `1px solid ${T.successBgStrong}`, color: T.success,
                    cursor: importProcessing ? 'not-allowed' : 'pointer',
                  }}
                >{importProcessing ? 'Importando...' : 'Importar'}</Button>
                <Button variant="ghost"
                  onClick={() => setImportStep('closed')}
                  disabled={importProcessing}
                  style={{ padding: '8px 14px', borderRadius: 6, fontSize: 12, background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-muted)', cursor: 'pointer' }}
                >Cancelar</Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Import result toast */}
      {importResult && (
        <div style={{
          padding: '6px 12px', fontSize: 12, fontWeight: 600,
          background: importResult.ok ? T.successBg : T.dangerBg,
          color: importResult.ok ? T.success : T.danger,
        }}>
          {importResult.msg}
        </div>
      )}

      {/* ═══ LIST VIEW ═══ */}
      {!selectedInteg ? (
        <div>
          {/* Import button */}
          {importStep === 'closed' && !loading && !pendingImport && (
            <Button variant="ghost"
              onClick={openImportPanel}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                padding: '8px 12px', background: 'transparent',
                border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)',
                color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.bgCard; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Importar Credenciais
            </Button>
          )}

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <div style={{
                width: 16, height: 16, border: '2px solid var(--border)',
                borderTopColor: 'var(--primary)', borderRadius: '50%',
                animation: 'spin 0.8s linear infinite', marginRight: 8,
              }} />
              <div style={{ padding: 8 }}><SkeletonBar width="50%" height={14} /></div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {filteredIntegrations.map(integ => {
                const st = statuses[integ.id] || 'unknown';
                const isConnected = st === 'configured' || st === 'verified';
                return (
                  <div
                    key={integ.id}
                    onClick={() => { setSelectedInteg(integ); setSaveResult(null); setFieldValues({}); setShowGuide(true); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', minHeight: 44,
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      borderLeft: isConnected ? `2px solid ${T.successBgStrong}` : '2px solid transparent',
                      background: isConnected ? T.successBg : 'transparent',
                      cursor: 'pointer', transition: 'background 0.12s',
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.bgCard; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = isConnected ? T.successBg : 'transparent'; }}
                  >
                    {/* Icon 28px */}
                    <div style={{
                      width: 28, height: 28, flexShrink: 0, borderRadius: 6,
                      background: `${integ.color}15`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <IntegIcon id={integ.id} size={16} />
                    </div>
                    {/* Name + description */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{integ.name}</span>
                        {/* Status dot */}
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                          background: isConnected ? T.success : T.borderHover,
                        }} />
                      </div>
                      <div style={{
                        fontSize: 11, color: 'var(--text-muted)', marginTop: 1,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>{integ.description}</div>
                    </div>
                    {/* Chevron */}
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0, opacity: 0.3 }}>
                      <path d="M5 3l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                );
              })}
            </div>
          )}

          {/* Security note — admin only */}
          {isAdmin.value && (
            <div style={{
              margin: '8px 12px 0', padding: '6px 10px', borderRadius: 6,
              background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
              fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5,
            }}>
              Credenciais salvas em <code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>~/.openclaw/.env</code> na máquina local. Nunca enviadas para servidores externos.
            </div>
          )}

          {/* Sync indicator moved to header */}
        </div>
      ) : (
        /* ═══ DETAIL/FORM VIEW ═══ */
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {/* Header: back arrow + name */}
          <div style={{
            padding: '8px 12px',
            display: 'flex', alignItems: 'center', gap: 8,
            borderBottom: '1px solid rgba(255,255,255,0.06)',
          }}>
            <Button variant="ghost"
              onClick={() => { setSelectedInteg(null); setFieldValues({}); setSaveResult(null); }}
              style={{
                background: 'none', border: 'none', color: 'var(--text-muted)',
                cursor: 'pointer', fontSize: 16, padding: '2px 4px', lineHeight: 1,
              }}
            >←</Button>
            <div style={{
              width: 28, height: 28, borderRadius: 6, flexShrink: 0,
              background: `${selectedInteg.color}15`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}><IntegIcon id={selectedInteg.id} size={16} /></div>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', flex: 1 }}>{selectedInteg.name}</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {getStatusBadge(selectedInteg.id)}
              {selectedInteg.docs && (
                <a href={selectedInteg.docs} target="_blank" rel="noopener" style={{
                  fontSize: 10, color: 'var(--primary)', textDecoration: 'none',
                  padding: '3px 8px', borderRadius: 4,
                  background: T.bgCard, border: `1px solid ${T.whiteAlpha8}`,
                }}>Docs</a>
              )}
            </div>
          </div>

          {/* How to get guide */}
          {showGuide && selectedInteg.howToGet && (
            <div style={{
              padding: '8px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
              fontSize: 11, lineHeight: 1.6, color: 'var(--text-muted)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)' }}>Como conseguir</span>
                <Button variant="ghost" onClick={() => setShowGuide(false)} style={{
                  background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 10,
                }}></Button>
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: 11 }}>{selectedInteg.howToGet}</pre>
            </div>
          )}
          {!showGuide && selectedInteg.howToGet && (
            <Button variant="ghost"
              onClick={() => setShowGuide(true)}
              style={{
                width: '100%', padding: '5px', background: 'none',
                border: 'none', borderBottom: '1px solid rgba(255,255,255,0.06)',
                color: 'var(--primary)', fontSize: 10, cursor: 'pointer', fontWeight: 600,
              }}
            >Como conseguir as credenciais ▼</Button>
          )}

          {/* Fields */}
          <div style={{ padding: '12px 12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {selectedInteg.fields.map(field => {
                const existing = existingKeys[field.key];
                const showVal = showValues[field.key];
                return (
                  <div key={field.key}>
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                      {field.label}
                      {field.required && <span style={{ color: T.danger, fontSize: 9 }}>*</span>}
                      {existing && <span style={{ fontSize: 9, color: T.success, fontWeight: 600 }}></span>}
                    </label>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        type={showVal ? 'text' : (field.type === 'email' || field.type === 'username' || field.type === 'text') ? 'text' : 'password'}
                        value={fieldValues[field.key] || ''}
                        onInput={(e: any) => setFieldValues(v => ({ ...v, [field.key]: e.target.value }))}
                        placeholder={existing || field.placeholder}
                        style={{
                          flex: 1, padding: '7px 10px', borderRadius: 6,
                          background: 'var(--bg)',
                          border: existing ? `1px solid ${T.successBgStrong}` : '1px solid var(--border)',
                          color: 'var(--text)', fontSize: 12, fontFamily: 'monospace', outline: 'none',
                        }}
                      />
                      {(field.type === 'api_key' || field.type === 'token' || field.type === 'password') && (
                        <Button variant="ghost"
                          onClick={() => setShowValues(v => ({ ...v, [field.key]: !v[field.key] }))}
                          style={{
                            padding: '7px 10px', borderRadius: 6,
                            background: T.bgCardHover, border: '1px solid var(--border)',
                            color: 'var(--text-muted)', cursor: 'pointer', fontSize: 14,
                          }}
 >{showVal ? '' : ''}</Button>
                      )}
                    </div>
                    <div style={{ fontSize: 9, color: 'var(--text-muted)', marginTop: 2, fontFamily: 'monospace', opacity: 0.5 }}>
                      {field.key}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
              {(() => {
                const hasNewValues = Object.values(fieldValues).some(v => v.trim());
                const isConnected = statuses[selectedInteg.id] === 'configured' || statuses[selectedInteg.id] === 'verified';
                return (
                  <>
                    <Button variant="ghost"
                      onClick={handleSave}
                      disabled={saving || !hasNewValues}
                      style={{
                        padding: '7px 18px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                        background: hasNewValues ? T.bgActive : T.bgCardHover,
                        border: 'none', color: hasNewValues ? '#fff' : 'var(--text-muted)',
                        cursor: hasNewValues && !saving ? 'pointer' : 'not-allowed',
                        opacity: saving ? 0.6 : 1,
                      }}
                    >
                      {saving ? 'Salvando...' : isConnected ? 'Atualizar' : 'Conectar'}
                    </Button>

                    {selectedInteg.testCommand && (isConnected || statuses[selectedInteg.id] === 'failed') && (
                      <Button variant="ghost"
                        onClick={() => handleTest(selectedInteg)}
                        disabled={statuses[selectedInteg.id] === 'testing'}
                        style={{
                          padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: T.bgCard, border: '1px solid rgba(255,255,255,0.1)',
                          color: 'var(--primary)', cursor: 'pointer',
                        }}
                      >
                        {statuses[selectedInteg.id] === 'testing' ? 'Testando...' : 'Testar'}
                      </Button>
                    )}

                    {isConnected && (
                      <Button variant="ghost"
                        onClick={() => handleDisconnect(selectedInteg)}
                        disabled={saving}
                        style={{
                          padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                          background: T.dangerBg, border: `1px solid ${T.dangerBgStrong}`,
                          color: T.danger, cursor: saving ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Desconectar
                      </Button>
                    )}
                  </>
                );
              })()}
            </div>

            {/* Security note */}
            {isAdmin.value && (
              <div style={{
                marginTop: 14, padding: '6px 10px', borderRadius: 6,
                background: T.dangerBg, border: `1px solid ${T.dangerBg}`,
                fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.5,
              }}>
                <strong>Segurança:</strong> Credenciais em <code style={{ background: T.bgCardHover, padding: '1px 3px', borderRadius: 3 }}>~/.openclaw/.env</code> — nunca enviadas externamente.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ IMPORT HISTORY ═══ */}
      {importHistory.length > 0 && (
        <div style={{ padding: '8px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>Importações recentes</div>
          {importHistory.map(req => {
            const statusMap: Record<string, { color: string; label: string }> = {
              pending: { color: T.warning, label: 'Pendente' },
              processing: { color: T.primary, label: 'Processando' },
              completed: { color: T.success, label: 'Concluído' },
              partial: { color: T.warning, label: 'Parcial' },
              failed: { color: T.danger, label: 'Falhou' },
            };
            const st = statusMap[req.status] || { color: T.textMuted, label: req.status };
            let integs: string[] = [];
            try { integs = JSON.parse(req.integrations); } catch {}
            let result: any = null;
            try { result = JSON.parse(req.result || 'null'); } catch {}
            const isExpanded = expandedHistory === req.id;
            return (
              <div key={req.id} style={{ marginBottom: 4 }}>
                <Button variant="ghost"
                  onClick={() => setExpandedHistory(isExpanded ? null : req.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 6px', borderRadius: 4, background: T.bgSubtle,
                    border: '1px solid rgba(255,255,255,0.04)', color: 'var(--text)', cursor: 'pointer',
                    fontSize: 11, textAlign: 'left',
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.color, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{integs.length} integrações</span>
                  <span style={{ fontSize: 9, color: st.color }}>{st.label}</span>
                  <span style={{ fontSize: 9, color: 'var(--text-muted)' }}>{new Date(req.created_at).toLocaleDateString('pt-BR')}</span>
                </Button>
                {isExpanded && result && (
                  <div style={{ padding: '6px 8px', margin: '2px 0 0 12px', fontSize: 10, color: 'var(--text-muted)', background: T.bgSubtle, borderRadius: 4 }}>
                    {result.error && <div style={{ color: T.danger }}>{result.error}</div>}
                    {result.imported_keys != null && <div>Chaves importadas: {result.imported_keys}</div>}
                    {result.details?.map((d: any, i: number) => (
                      <div key={i}>{d.integration}: {d.status} {d.keys ? `(${d.keys} chaves)` : ''} {d.error ? `— ${d.error}` : ''}</div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Source paths footer — admin only */}
      {isAdmin.value && (
        <div style={{
          marginTop: 20, padding: '10px 14px', borderRadius: 8,
          background: T.bgSubtle, border: '1px solid var(--border)',
          fontSize: 10, color: 'var(--text-muted)', lineHeight: 1.8,
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 11 }}>Fonte dos dados</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>{dataDir}/workspace/.env</code> — Chaves de API e credenciais externas</div>
          <div><code style={{ background: T.bgCardHover, padding: '1px 4px', borderRadius: 3 }}>openclaw.json → env.vars</code> — Chaves de LLM (Anthropic, OpenAI, etc.)</div>
          <div style={{ marginTop: 4, opacity: 0.7 }}>Leitura e escrita via SSH direto no runner</div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
